import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { getScoringContent, GradeBand } from '@/services/api/scoringContent';

export const SCORING_CONTENT_QUERY_KEY = ['scoring-content'];

/** Default grade bands (matches scoreToGrade.ts hardcoded thresholds) */
const DEFAULT_GRADE_BANDS: Record<string, GradeBand> = {
  A: { min: 86, color: '#f3f3f3', label: 'Excellent — minimal concerns' },
  B: { min: 77, color: '#5AE9FF', label: 'Good — minor issues' },
  C: { min: 68, color: '#FFF981', label: 'Fair — moderate concerns' },
  D: { min: 59, color: '#FF9A4D', label: 'Below Average — significant issues' },
  F: { min: 0, color: '#DB5282', label: 'Poor — major concerns' },
};

/**
 * Hook to load user-facing scoring content (gradeBands, etc.) from the public
 * /scoring-content endpoint (available to all authenticated users).
 */
export const useScoringConfig = () => {
  const query = useQuery({
    queryKey: SCORING_CONTENT_QUERY_KEY,
    queryFn: getScoringContent,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  const gradeBands = query.data?.gradeBands ?? DEFAULT_GRADE_BANDS;

  // Config-aware score-to-grade function (sorted descending by min threshold)
  const scoreToGrade = useCallback(
    (score: number): string => {
      const sorted = Object.entries(gradeBands).sort(([, a], [, b]) => b.min - a.min);
      for (const [grade, band] of sorted) {
        if (score >= band.min) return grade;
      }
      return 'F';
    },
    [gradeBands],
  );

  return {
    gradeBands,
    scoreToGrade,
    isLoading: query.isLoading,
  };
};
